// src/components/RawDatabaseQueryPage.js
//
// Query + CSV export over the Raw Database (referral_submissions). Filters are
// ADD-ON: by default you see only a search box and an "Add filter" picker —
// each filter you choose appears as a compact, removable control, so the panel
// only ever shows what you've chosen to filter on. Read-only; nothing is saved.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { getReferralSubmissionsPath } from "../services/firestorePaths";
import {
  DISEASE_OPTIONS,
  diagnosisLabel,
  surveyConditionLabel,
} from "../constants/diseaseOptions";
import {
  ArrowLeft,
  Download,
  Search,
  X,
  Plus,
  Loader2,
  HeartPulse,
} from "lucide-react";

// Call-duty statuses (kept in sync with RawDatabasePage).
const CALL_STATUSES = [
  { key: "new", label: "New" },
  { key: "called", label: "Called" },
  { key: "no_answer", label: "No Answer" },
  { key: "callback", label: "Callback" },
  { key: "not_interested", label: "Not Interested" },
  { key: "enrolled", label: "Enrolled" },
];
const statusLabel = (key) =>
  (CALL_STATUSES.find((s) => s.key === key) || CALL_STATUSES[0]).label;

// Conditions the questionnaire can capture — the STORED names (kept in sync
// with ReferralQuestionnairePage's ALL_CONDITIONS); shown via surveyConditionLabel.
const CONDITION_OPTIONS = [
  "Asthma",
  "COPD",
  "Lung Nodules",
  "Lung Cancer",
  "Bronchiectasis",
  "Pulmonary Fibrosis",
  "Sleep Apnea",
  "Diabetes",
  "Sarcoidosis",
  "Heart Disease",
  "Cancer (other)",
];

const YESNO_NA = [
  { v: "any", l: "Any" },
  { v: "yes", l: "Yes" },
  { v: "no", l: "No" },
  { v: "not answered", l: "Not answered" },
];

const conditionLabel = (c) =>
  !c ? "" : typeof c === "string" ? c : c.name || c.condition || "";
const conditionDetail = (c) => {
  if (!c || typeof c === "string") return "";
  if (c.exacerbations != null && c.exacerbations !== "")
    return `Exacerbations: ${c.exacerbations}`;
  if (c.glp1ForDiabetes) return `GLP-1: ${c.glp1ForDiabetes}`;
  if (c.usingCpap) return `C-PAP: ${c.usingCpap}`;
  return "";
};

const millis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
};
const fmtTime = (ts) => {
  const m = millis(ts);
  return m ? new Date(m).toLocaleString() : "";
};

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";
const miniInputCls =
  "px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";
const miniSelectCls = miniInputCls + " appearance-none cursor-pointer pr-7";

// ---------------------------------------------------------------------------
// Filter definitions. The `f` state holds one entry per VALUE key (so the
// filtering logic stays a flat read). A def declares how it renders and which
// value keys it owns (so removing it can reset them).
// ---------------------------------------------------------------------------
const FILTER_DEFS = [
  {
    key: "callStatus",
    label: "Call Status",
    type: "select",
    valueKeys: ["callStatus"],
    options: [{ v: "all", l: "All" }, ...CALL_STATUSES.map((s) => ({ v: s.key, l: s.label }))],
  },
  {
    key: "diagnosis",
    label: "Diagnosis",
    type: "select",
    valueKeys: ["diagnosis"],
    options: [{ v: "any", l: "Any" }, ...DISEASE_OPTIONS.map((d) => ({ v: d, l: diagnosisLabel(d) }))],
  },
  {
    key: "condition",
    label: "Reported Condition",
    type: "select",
    valueKeys: ["condition"],
    options: [{ v: "any", l: "Any" }, ...CONDITION_OPTIONS.map((c) => ({ v: c, l: surveyConditionLabel(c) }))],
  },
  {
    key: "signed",
    label: "Signed Consent",
    type: "select",
    valueKeys: ["signed"],
    options: [
      { v: "any", l: "Any" },
      { v: "yes", l: "Signed" },
      { v: "no", l: "Not signed" },
    ],
  },
  { key: "weightInterest", label: "Weight Interest", type: "select", valueKeys: ["weightInterest"], options: YESNO_NA },
  { key: "onGlp1", label: "On GLP-1", type: "select", valueKeys: ["onGlp1"], options: YESNO_NA },
  { key: "bmi", label: "BMI", type: "range", valueKeys: ["bmiMin", "bmiMax"], minKey: "bmiMin", maxKey: "bmiMax" },
  { key: "weight", label: "Weight (lbs)", type: "range", valueKeys: ["weightMin", "weightMax"], minKey: "weightMin", maxKey: "weightMax" },
  { key: "date", label: "Submitted", type: "daterange", valueKeys: ["dateFrom", "dateTo"], fromKey: "dateFrom", toKey: "dateTo" },
];

const DEFAULTS = {
  search: "",
  callStatus: "all",
  diagnosis: "any",
  condition: "any",
  signed: "any",
  weightInterest: "any",
  onGlp1: "any",
  bmiMin: "",
  bmiMax: "",
  weightMin: "",
  weightMax: "",
  dateFrom: "",
  dateTo: "",
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
  </div>
);

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const RawDatabaseQueryPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(DEFAULTS);
  const [activeKeys, setActiveKeys] = useState([]); // which filter defs are shown

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, getReferralSubmissionsPath()),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => millis(b.submittedAt) - millis(a.submittedAt));
        setSubmissions(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Raw DB query subscribe failed:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const addFilter = (defKey) => {
    if (!defKey || activeKeys.includes(defKey)) return;
    setActiveKeys((prev) => [...prev, defKey]);
  };

  const removeFilter = (defKey) => {
    const def = FILTER_DEFS.find((d) => d.key === defKey);
    setActiveKeys((prev) => prev.filter((k) => k !== defKey));
    if (def) {
      setF((prev) => {
        const next = { ...prev };
        def.valueKeys.forEach((vk) => (next[vk] = DEFAULTS[vk]));
        return next;
      });
    }
  };

  const clearAll = () => {
    setF(DEFAULTS);
    setActiveKeys([]);
  };

  const results = useMemo(() => {
    const term = f.search.trim().toLowerCase();
    const dateFromMs = f.dateFrom ? new Date(f.dateFrom).getTime() : null;
    const dateToMs = f.dateTo
      ? new Date(f.dateTo).getTime() + 24 * 60 * 60 * 1000 - 1
      : null;

    return submissions.filter((s) => {
      if (term) {
        const hay = `${s.name || ""} ${s.phone || ""} ${s.email || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (f.callStatus !== "all" && (s.callStatus || "new") !== f.callStatus) return false;
      if (f.diagnosis !== "any" && !(Array.isArray(s.diagnosis) && s.diagnosis.includes(f.diagnosis))) return false;
      if (f.weightInterest !== "any" && (s.weightManagementInterest || "not answered") !== f.weightInterest) return false;
      if (f.onGlp1 !== "any" && (s.onGlp1WeightMgmt || "not answered") !== f.onGlp1) return false;
      if (f.condition !== "any") {
        const names = (Array.isArray(s.conditions) ? s.conditions : []).map(conditionLabel);
        if (!names.includes(f.condition)) return false;
      }
      if (f.signed === "yes" && !s.consentPdfUrl) return false;
      if (f.signed === "no" && s.consentPdfUrl) return false;

      if (f.bmiMin !== "" && (s.bmi == null || Number(s.bmi) < Number(f.bmiMin))) return false;
      if (f.bmiMax !== "" && (s.bmi == null || Number(s.bmi) > Number(f.bmiMax))) return false;
      if (f.weightMin !== "" && (s.weightLbs == null || Number(s.weightLbs) < Number(f.weightMin))) return false;
      if (f.weightMax !== "" && (s.weightLbs == null || Number(s.weightLbs) > Number(f.weightMax))) return false;

      const ms = millis(s.submittedAt);
      if (dateFromMs != null && ms < dateFromMs) return false;
      if (dateToMs != null && ms > dateToMs) return false;

      return true;
    });
  }, [submissions, f]);

  const exportCSV = () => {
    if (!results.length) return;
    const headers = [
      "Name", "DOB", "Phone", "Email", "Address", "Call Status", "Diagnosis",
      "Weight Interest", "Height", "Weight (lbs)", "BMI", "On GLP-1",
      "Reported Conditions", "Signed", "Source", "Submitted At",
    ];
    const lines = results.map((s) =>
      [
        s.name,
        s.dob,
        s.phone,
        s.email,
        s.address,
        statusLabel(s.callStatus || "new"),
        Array.isArray(s.diagnosis) ? s.diagnosis.map(diagnosisLabel).join("; ") : "",
        s.weightManagementInterest,
        s.height,
        s.weightLbs,
        s.bmi,
        s.onGlp1WeightMgmt,
        Array.isArray(s.conditions)
          ? s.conditions
              .map((c) => {
                const d = conditionDetail(c);
                const name = surveyConditionLabel(conditionLabel(c));
                return d ? `${name} (${d})` : name;
              })
              .join("; ")
          : "",
        s.consentPdfUrl ? "Yes" : "No",
        s.source,
        fmtTime(s.submittedAt),
      ]
        .map(csvEscape)
        .join(",")
    );
    const csv = [headers.map(csvEscape).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "raw_database_query.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const availableDefs = FILTER_DEFS.filter((d) => !activeKeys.includes(d.key));

  const renderControl = (def) => {
    if (def.type === "select") {
      return (
        <select
          className={miniSelectCls}
          value={f[def.key]}
          onChange={(e) => set(def.key, e.target.value)}
        >
          {def.options.map((o) => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
      );
    }
    if (def.type === "range") {
      return (
        <div className="flex items-center gap-1">
          <input className={miniInputCls + " w-16"} inputMode="decimal" placeholder="min" value={f[def.minKey]} onChange={(e) => set(def.minKey, e.target.value)} />
          <span className="text-slate-500 text-xs">–</span>
          <input className={miniInputCls + " w-16"} inputMode="decimal" placeholder="max" value={f[def.maxKey]} onChange={(e) => set(def.maxKey, e.target.value)} />
        </div>
      );
    }
    // daterange
    return (
      <div className="flex items-center gap-1">
        <input type="date" className={miniInputCls + " [color-scheme:dark]"} value={f[def.fromKey]} onChange={(e) => set(def.fromKey, e.target.value)} />
        <span className="text-slate-500 text-xs">–</span>
        <input type="date" className={miniInputCls + " [color-scheme:dark]"} value={f[def.toKey]} onChange={(e) => set(def.toKey, e.target.value)} />
      </div>
    );
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/coordinator/raw-database")}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-semibold">
                Raw Database
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-white">
                Query &amp; Export
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
              {results.length} of {submissions.length} match
            </span>
            <button
              onClick={exportCSV}
              disabled={!results.length}
              className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Compact filter bar */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search (always visible) */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              <input
                value={f.search}
                onChange={(e) => set("search", e.target.value)}
                placeholder="Search name, phone, or email"
                className={inputCls + " pl-9"}
              />
            </div>

            {/* Add filter picker */}
            {availableDefs.length > 0 && (
              <div className="relative">
                <select
                  value=""
                  onChange={(e) => {
                    addFilter(e.target.value);
                    e.target.value = "";
                  }}
                  className={miniSelectCls + " pl-3"}
                >
                  <option value="" disabled>
                    + Add filter
                  </option>
                  {availableDefs.map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
                <Plus className="w-3.5 h-3.5 text-purple-300 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}

            {(activeKeys.length > 0 || f.search) && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/15 border border-rose-400/30 text-[11px] font-medium text-rose-200 hover:bg-rose-500/25 transition"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>

          {/* Active filter chips */}
          {activeKeys.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {activeKeys.map((k) => {
                const def = FILTER_DEFS.find((d) => d.key === k);
                if (!def) return null;
                return (
                  <div
                    key={k}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] pl-3 pr-1.5 py-1.5"
                  >
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">
                      {def.label}
                    </span>
                    {renderControl(def)}
                    <button
                      onClick={() => removeFilter(k)}
                      title="Remove filter"
                      className="p-1 rounded-md text-slate-400 hover:text-rose-300 transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Results table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.6fr_1.2fr_1fr_1.4fr_1fr_1fr] px-4 py-2.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400 border-b border-white/10 bg-slate-950/30">
            <div>Patient</div>
            <div>Phone</div>
            <div>Status</div>
            <div>Diagnosis</div>
            <div>Signed</div>
            <div>Submitted</div>
          </div>

          <div className="divide-y divide-white/5 max-h-[62vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : results.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                No submissions match your filters.
              </div>
            ) : (
              results.map((s) => (
                <div
                  key={s.id}
                  className="grid grid-cols-1 md:grid-cols-[1.6fr_1.2fr_1fr_1.4fr_1fr_1fr] gap-1.5 md:gap-3 px-4 py-2.5 hover:bg-white/[0.04] transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {s.name || "Unknown patient"}
                    </div>
                    {s.email && (
                      <div className="text-[10px] text-slate-400 truncate">{s.email}</div>
                    )}
                  </div>
                  <div className="text-xs font-mono text-slate-200 flex items-center">
                    {s.phone || "—"}
                  </div>
                  <div className="flex items-center">
                    <span className="text-[11px] text-slate-300">
                      {statusLabel(s.callStatus || "new")}
                    </span>
                  </div>
                  <div className="flex items-center min-w-0">
                    {Array.isArray(s.diagnosis) && s.diagnosis.length ? (
                      <div className="flex flex-wrap gap-1">
                        {s.diagnosis.map((d) => (
                          <span
                            key={d}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 border border-purple-300/30 text-purple-200"
                          >
                            <HeartPulse className="w-2.5 h-2.5" />
                            {diagnosisLabel(d)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 italic">—</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-200 flex items-center">
                    {s.consentPdfUrl ? "Yes" : "No"}
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center">
                    {fmtTime(s.submittedAt) || "—"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawDatabaseQueryPage;
